<template>
  <section class="admin-page">

    <!-- Header -->
    <header class="admin-header">
      <h1>Audit Logs</h1>
      <p>Immutable record of critical platform actions and security events.</p>
    </header>

    <!-- Filters -->
    <section class="filters">
      <input
        v-model="filters.search"
        placeholder="Search by user, action, or resource…"
      />

      <select v-model="filters.type">
        <option value="">All Actions</option>
        <option value="creator">Creator Actions</option>
        <option value="admin">Admin Actions</option>
        <option value="system">System Events</option>
        <option value="security">Security</option>
      </select>

      <input type="date" v-model="filters.from" />
      <input type="date" v-model="filters.to" />
    </section>

    <!-- Logs Table -->
    <section class="card">
      <table class="audit-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Actor</th>
            <th>Role</th>
            <th>Action</th>
            <th>Target</th>
            <th>IP</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
         <tr v-for="log in filteredLogs" :key="log.ulog_id">
            <td>{{ log.ulog_datetime }}</td>
            <td>{{ log.user_name }}</td>
            <td>
              <span class="role-pill">{{ log.user_type }}</span>
            </td>
            <td>{{ log.ulog_action }}</td>
            <td>{{ log.ulog_txt }}</td>
            <td class="mono">{{ log.ulog_ip }}</td>
            <td>
              <span :class="['status-pill', log.ulog_status]">
                {{ log.ulog_status }}
              </span>
            </td>
          </tr>

        </tbody>
      </table>

      <div v-if="filteredLogs.length === 0" class="empty">
        No logs found for selected filters.
      </div>
    </section>

  </section>
</template>

<script>
import { getAuditLogs } from "@/apis/auditapis";

export default {
  name: "AdminAuditLogs",

  data() {
    return {
      page: 0,

      filters: {
        search: "",
        type: "",
        from: "",
        to: ""
      },

      logs: []
    };
  },

  async mounted() {
    await this.loadLogs();
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.page = 0;
        this.loadLogs();
      }
    }
  },

  methods: {

    async loadLogs() {

      const res = await getAuditLogs({
        page_no: this.page,
        search: this.filters.search,
        type: this.filters.type,
        from: this.filters.from,
        to: this.filters.to
      });

      if (!res.status) {
        return;
      }

      this.logs = res.records || [];
    }

  },

  computed: {
    filteredLogs() {
      return this.logs; // ✅ backend already filtered
    }
  }
};
</script>

<style scoped>
.admin-page {
  max-width: 1440px;
  margin: auto;
  padding: 48px;
}

.admin-header h1 {
  font-size: 36px;
  font-weight: 800;
}

.admin-header p {
  color: #64748b;
  margin-top: 6px;
}

/* Filters */
.filters {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 12px;
  margin: 32px 0;
}

.filters input,
.filters select {
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  font-size: 14px;
}

/* Card */
.card {
  background: white;
  border-radius: 24px;
  padding: 32px;
  border: 1px solid #e5e7eb;
}

/* Table */
.audit-table {
  width: 100%;
  border-collapse: collapse;
}

.audit-table th,
.audit-table td {
  padding: 14px;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
}

.mono {
  font-family: monospace;
}

/* Pills */
.role-pill {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  font-weight: 700;
}

.status-pill {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 800;
  text-transform: uppercase;
}

.status-pill.success {
  background: #ecfdf5;
  color: #047857;
}

.status-pill.warning {
  background: #fffbeb;
  color: #92400e;
}

.status-pill.failed {
  background: #fef2f2;
  color: #991b1b;
}

/* Empty */
.empty {
  padding: 24px;
  text-align: center;
  font-size: 14px;
  color: #64748b;
}

/* Mobile */
@media (max-width: 900px) {
  .filters {
    grid-template-columns: 1fr;
  }

  .admin-page {
    padding: 24px;
  }
}
/* ===============================
   ADMIN AUDIT LOGS – MOBILE PATCH
   =============================== */
@media (max-width: 640px) {

  /* Page padding */
  .admin-page {
    padding: 20px 16px 72px;
  }

  /* Header */
  .admin-header h1 {
    font-size: 26px;
    line-height: 1.2;
  }

  .admin-header p {
    font-size: 14px;
  }

  /* Filters → stacked */
  .filters {
    gap: 10px;
    margin: 20px 0;
  }

  .filters input,
  .filters select {
    padding: 12px;
    font-size: 14px;
    border-radius: 10px;
  }

  /* Card */
  .card {
    padding: 16px;
    border-radius: 18px;
  }

  /* Table → stacked log cards */
  .audit-table thead {
    display: none;
  }

  .audit-table,
  .audit-table tbody,
  .audit-table tr,
  .audit-table td {
    display: block;
    width: 100%;
  }

  .audit-table tr {
    padding: 14px 0;
    border-bottom: 1px solid #f1f5f9;
  }

  .audit-table td {
    padding: 6px 0;
    border: none;
    font-size: 14px;
    word-break: break-word;
  }

  /* Emphasis tweaks */
  .audit-table td:first-child {
    font-weight: 700;
    font-size: 13px;
    color: #64748b;
  }

  .mono {
    font-size: 12px;
  }

  /* Pills */
  .role-pill,
  .status-pill {
    font-size: 10px;
    padding: 4px 8px;
  }

  /* Empty state */
  .empty {
    padding: 20px 10px;
    font-size: 14px;
  }
}

</style>
